import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useSelector } from 'react-redux';
import config from 'constants/apiConfig';
import { selectGroupGuid, selectUserGuid } from 'slices/commonSelectors';
import { camelToSnakeCase } from 'utils/caseConversion';
import { useCfaDemoEnabled } from 'utils/featureFlags';
import {
  formatStoreListLabel,
  storesInCone,
} from 'components/MapPage/CorporateViewTabs/LayersTab/cfa-demo/historicalHurricanesHeleneData';
import {
  heleneConeResourceNotesMarker,
  isHeleneConeResourceSet,
} from 'components/MapPage/CorporateViewTabs/NotificationsTab/memphisThreatNotification';
import {
  keys as resourceSetKeys,
  mapResourceSetToEditPayload,
  type ResourceSet,
} from 'components/IAP/hooks/resourceSetHooks';

const CFA_RESOURCE_KIND = 'Facilities';
const CFA_RESOURCE_TYPE = 'Restaurant';
export const CFA_HELENE_SYMBOLOGY = 'chickfilaLocation';

/** Shared locks so React Strict Mode remounts do not double-create. */
const ensureLocks = new Map<string, Promise<unknown>>();

async function patchMissingHeleneSymbology({
  resources,
  groupGuid,
  userGuid,
}: {
  resources: ResourceSet[];
  groupGuid: string;
  userGuid: string;
}): Promise<boolean> {
  const missing = resources.filter(
    (resource) => isHeleneConeResourceSet(resource) && !resource.symbology
  );
  if (missing.length === 0) return false;

  await Promise.all(
    missing.map((resource) => {
      const payload = {
        ...mapResourceSetToEditPayload(resource.resource_set_id, resource),
        symbology: CFA_HELENE_SYMBOLOGY,
      };
      const { members: _members, ...dataForBackend } = payload;
      return axios.put(
        `${config.apiGateway.ics_resource_sets}/${resource.resource_set_id}`,
        {
          ...camelToSnakeCase(dataForBackend),
          user_guid: userGuid,
          group_guid: groupGuid,
          incident_id: null,
        }
      );
    })
  );
  return true;
}

async function ensureHeleneConeResourceSetsForGroup({
  groupGuid,
  userGuid,
  queryClient,
}: {
  groupGuid: string;
  userGuid: string;
  queryClient: ReturnType<typeof useQueryClient>;
}): Promise<void> {
  const resp = await axios.get<ResourceSet[]>(
    `${config.apiGateway.ics_resource_sets}/${groupGuid}`
  );
  const existing = resp.data ?? [];
  const existingMarkers = new Set(
    existing
      .filter(isHeleneConeResourceSet)
      .map((resource) => resource.notes as string)
  );

  const toCreate = storesInCone().filter(
    (store) => !existingMarkers.has(heleneConeResourceNotesMarker(store.id))
  );

  let didMutate = false;

  if (toCreate.length > 0) {
    await Promise.all(
      toCreate.map((store) => {
        const notes = heleneConeResourceNotesMarker(store.id);
        return axios.post(config.apiGateway.ics_resource_sets, {
          name: formatStoreListLabel(store),
          description: `${store.name} (${store.city}, ${store.state}) — CFA store inside the Helene forecast cone.`,
          kind: CFA_RESOURCE_KIND,
          type: CFA_RESOURCE_TYPE,
          quantity: 1,
          status: 'In Organization',
          asset_status: 'PMC',
          asset_status_timestamp: new Date().toISOString(),
          address: `${store.city}, ${store.state}`,
          symbology: CFA_HELENE_SYMBOLOGY,
          notes,
          geometry: {
            type: 'Point',
            coordinates: [store.lon, store.lat],
          },
          incidents: [],
          archived: false,
          group_guid: groupGuid,
          user_guid: userGuid,
          incident_id: null,
        });
      })
    );
    didMutate = true;
  }

  const patched = await patchMissingHeleneSymbology({
    resources: existing,
    groupGuid,
    userGuid,
  });
  if (patched) didMutate = true;

  if (didMutate) {
    await queryClient.invalidateQueries({
      queryKey: resourceSetKeys.all,
    });
  }
}

/**
 * When CFA is on, ensure each Helene cone store exists as a full Hub resource
 * set.
 */
export function useEnsureHeleneConeResourceSets() {
  const cfaEnabled = useCfaDemoEnabled();
  const groupGuid = useSelector(selectGroupGuid);
  const userGuid = useSelector(selectUserGuid);
  const queryClient = useQueryClient();
  const completedRef = useRef<string | null>(null);

  useEffect(() => {
    if (!cfaEnabled || !groupGuid || !userGuid) {
      return;
    }

    const scopeKey = `${groupGuid}:${userGuid}`;
    if (completedRef.current === scopeKey) {
      return;
    }

    let cancelled = false;
    let ensurePromise = ensureLocks.get(scopeKey);
    if (!ensurePromise) {
      ensurePromise = ensureHeleneConeResourceSetsForGroup({
        groupGuid,
        userGuid,
        queryClient,
      })
        .catch((error) => {
          console.error(
            '[CFA Memphis] Failed to ensure Helene cone resource sets',
            error
          );
          throw error;
        })
        .finally(() => {
          ensureLocks.delete(scopeKey);
        });
      ensureLocks.set(scopeKey, ensurePromise);
    }

    void ensurePromise
      .then(() => {
        if (!cancelled) {
          completedRef.current = scopeKey;
        }
      })
      .catch(() => {
        // Allow retry on next effect.
      });

    return () => {
      cancelled = true;
    };
  }, [cfaEnabled, groupGuid, userGuid, queryClient]);
}
